"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

import { serializeProduct } from "@/lib/serialize-product";
import type { CartItem, Product, ProductVariant } from "@/types";

type CartState = {
  cartOwnerId: string | null;
  items: CartItem[];
  savedItems: CartItem[];
  couponCode: string | null;
  couponDiscount: number;
  addItem: (product: Product, quantity?: number, selectedVariant?: ProductVariant | null) => void;
  removeItem: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  saveForLater: (productId: string, variantId?: string) => void;
  moveToCart: (productId: string, variantId?: string) => void;
  removeSavedItem: (productId: string, variantId?: string) => void;
  moveToWishlist: (productId: string, variantId?: string) => void;
  applyCoupon: (code: string, discount: number) => void;
  clearCoupon: () => void;
  clearCart: () => void;
  subtotal: () => number;
  count: () => number;
};

let syncUserId: string | null = null;

export function setCartSyncUser(userId: string | null) {
  syncUserId = userId;
}

async function syncUpsert(productId: string, quantity: number, variantId?: string) {
  if (!syncUserId) return;
  try {
    await fetch("/api/cart", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId, quantity, variantId: variantId || undefined })
    });
  } catch {
    // best-effort sync; local state remains the source of truth for the UI
  }
}

async function syncRemove(productId: string, variantId?: string) {
  if (!syncUserId) return;
  try {
    await fetch("/api/cart", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId, variantId: variantId || undefined })
    });
  } catch {
    // ignore network errors
  }
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      cartOwnerId: null,
      items: [],
      savedItems: [],
      couponCode: null,
      couponDiscount: 0,
      addItem: (product, quantity = 1, selectedVariant = null) => {
        const variantId = selectedVariant?.id;
        const matchItem = (item: CartItem) =>
          item.product.id === product.id &&
          (variantId ? item.selectedVariant?.id === variantId : !item.selectedVariant);

        const saved = get().savedItems.find(matchItem);
        if (saved) {
          const existingInCart = get().items.find(matchItem);
          const nextQuantity = existingInCart
            ? Math.min(existingInCart.quantity + quantity, 20)
            : Math.min(saved.quantity, 20);
          set({
            savedItems: get().savedItems.filter((item) => !matchItem(item)),
            items: existingInCart
              ? get().items.map((i) => (matchItem(i) ? { ...i, quantity: nextQuantity } : i))
              : [...get().items, { product, quantity: nextQuantity, selectedVariant: selectedVariant ?? undefined }]
          });
          void syncUpsert(product.id, nextQuantity, variantId);
          return;
        }

        const items = get().items;
        const current = items.find(matchItem);

        if (current) {
          const nextQuantity = Math.min(current.quantity + quantity, 20);
          set({
            items: items.map((item) =>
              matchItem(item) ? { ...item, quantity: nextQuantity } : item
            )
          });
          void syncUpsert(product.id, nextQuantity, variantId);
          return;
        }

        set({ items: [...items, { product, quantity, selectedVariant: selectedVariant ?? undefined }] });
        void syncUpsert(product.id, quantity, variantId);
      },
      removeItem: (productId, variantId) => {
        set({
          items: get().items.filter((item) =>
            variantId
              ? !(item.product.id === productId && item.selectedVariant?.id === variantId)
              : (item.product.id !== productId || !!item.selectedVariant)
          )
        });
        void syncRemove(productId, variantId);
      },
      updateQuantity: (productId, quantity, variantId) => {
        const clamped = Math.max(1, Math.min(quantity, 20));
        set({
          items: get().items.map((item) => {
            const matches = variantId
              ? item.product.id === productId && item.selectedVariant?.id === variantId
              : item.product.id === productId && !item.selectedVariant;
            return matches ? { ...item, quantity: clamped } : item;
          })
        });
        void syncUpsert(productId, clamped, variantId);
      },
      saveForLater: (productId, variantId) => {
        const matchItem = (i: CartItem) =>
          i.product.id === productId &&
          (variantId ? i.selectedVariant?.id === variantId : !i.selectedVariant);

        const item = get().items.find(matchItem);
        if (!item) return;
        set({
          items: get().items.filter((i) => !matchItem(i)),
          savedItems: [...get().savedItems.filter((i) => !matchItem(i)), item]
        });
        void syncRemove(productId, variantId);
      },
      moveToCart: (productId, variantId) => {
        const matchItem = (i: CartItem) =>
          i.product.id === productId &&
          (variantId ? i.selectedVariant?.id === variantId : !i.selectedVariant);

        const savedItem = get().savedItems.find(matchItem);
        if (!savedItem) return;

        const currentItems = get().items;
        const existingInCart = currentItems.find(matchItem);
        const nextQuantity = existingInCart
          ? Math.min(existingInCart.quantity + savedItem.quantity, 20)
          : savedItem.quantity;

        const newItems = existingInCart
          ? currentItems.map((i) => (matchItem(i) ? { ...i, quantity: nextQuantity } : i))
          : [...currentItems, savedItem];

        set({
          savedItems: get().savedItems.filter((i) => !matchItem(i)),
          items: newItems
        });
        void syncUpsert(productId, nextQuantity, variantId);
      },
      removeSavedItem: (productId, variantId) => {
        const matchItem = (i: CartItem) =>
          i.product.id === productId &&
          (variantId ? i.selectedVariant?.id === variantId : !i.selectedVariant);

        set({
          savedItems: get().savedItems.filter((i) => !matchItem(i))
        });
      },
      moveToWishlist: (productId, variantId) => {
        const matchItem = (i: CartItem) =>
          i.product.id === productId &&
          (variantId ? i.selectedVariant?.id === variantId : !i.selectedVariant);

        set({
          items: get().items.filter((i) => !matchItem(i)),
          savedItems: get().savedItems.filter((i) => !matchItem(i))
        });
        void syncRemove(productId, variantId);
      },
      applyCoupon: (code, discount) => set({ couponCode: code, couponDiscount: discount }),
      clearCoupon: () => set({ couponCode: null, couponDiscount: 0 }),
      clearCart: () => {
        const items = get().items;
        set({ items: [], savedItems: [], couponCode: null, couponDiscount: 0, cartOwnerId: null });
        items.forEach((item) => void syncRemove(item.product.id, item.selectedVariant?.id));
      },
      subtotal: () => get().items.reduce((total, item) => total + (item.selectedVariant?.price ?? item.product.price) * item.quantity, 0),
      count: () => get().items.reduce((total, item) => total + item.quantity, 0)
    }),
    {
      name: "mova-ethiopia-cart",
      version: 2,
      migrate: () => ({
        cartOwnerId: null,
        items: [],
        savedItems: [],
        couponCode: null,
        couponDiscount: 0,
      }),
    }
  )
);

type ServerCartRow = {
  productId: string;
  variantId?: string;
  quantity: number;
  product: Parameters<typeof serializeProduct>[0];
};

/** Called on sign-in: sets the user's saved cart from the server as the sole source of truth. */
export async function hydrateCartFromServer(userId: string) {
  try {
    // If a brand new account registration just finished, force 100% clean/empty cart
    if (typeof window !== "undefined" && window.sessionStorage?.getItem("mova_new_registration") === "true") {
      try {
        window.sessionStorage.removeItem("mova_new_registration");
      } catch {
        // ignore
      }
      useCartStore.setState({
        items: [],
        savedItems: [],
        cartOwnerId: userId,
        couponCode: null,
        couponDiscount: 0,
      });
      setCartSyncUser(userId);
      return;
    }

    const response = await fetch("/api/cart");
    if (!response.ok) {
      setCartSyncUser(userId);
      return;
    }

    const data = (await response.json()) as { items?: ServerCartRow[] };
    const rawItems = Array.isArray(data?.items) ? data.items : [];
    const serverItems: CartItem[] = rawItems
      .filter((row) => row && row.product)
      .map((row) => {
        const product = serializeProduct(row.product);
        const variant = row.variantId ? product.variants?.find((v) => v.id === row.variantId) : undefined;
        return {
          product,
          quantity: row.quantity,
          selectedVariant: variant,
        };
      });

    // The user's database cart is the absolute single source of truth.
    // Stale local items from other sessions or unauthenticated browser states are NEVER injected.
    useCartStore.setState({ items: serverItems, cartOwnerId: userId });
    setCartSyncUser(userId);
  } catch {
    setCartSyncUser(userId);
  }
}
